/**
 * Starts the Experiences App booking picker (blocks/experience-booking.liquid), GV-14.
 *
 * The app ships one script and one stylesheet from its own CDN, and a global, ExperiencesApp, whose
 * enableBooking() draws the date and places picker into an element and adds the booking to the
 * basket itself. Live loads the script parser-blocking and calls it inline; here both are added
 * after the page has parsed, and the call waits for the script's load event, so the product page
 * paints first. The settings come from the JSON the block renders, so no store data is written
 * here and nothing is formatted here.
 *
 * If the script fails to load or throws, the block says so in its own words (rendered by Liquid),
 * rather than leaving a blank space where the picker should be.
 */

const SCRIPT = 'https://prod-v2.experiencesapp.services/storefront/embedded.js';
const STYLE = 'https://prod-v2.experiencesapp.services/storefront/embedded.css';

/** @type {Promise<any> | null} */
let loading = null;

function loadApp() {
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${STYLE}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = STYLE;
      document.head.append(link);
    }
    if (/** @type {any} */ (window).ExperiencesApp) return resolve(/** @type {any} */ (window).ExperiencesApp);
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.addEventListener('load', () => {
      const app = /** @type {any} */ (window).ExperiencesApp;
      app ? resolve(app) : reject(new Error('ExperiencesApp missing after load'));
    });
    script.addEventListener('error', () => reject(new Error('Experiences script failed to load')));
    document.head.append(script);
  });
  return loading;
}

function fail(root) {
  root.removeAttribute('aria-busy');
  root.querySelector('.experience-booking__loading')?.setAttribute('hidden', '');
  root.querySelector('.experience-booking__failed')?.removeAttribute('hidden');
}

async function start(root) {
  if (root.dataset.started) return;
  root.dataset.started = 'true';
  const configEl = root.parentElement?.querySelector('script[data-experience-booking-config]');
  let config;
  try {
    config = JSON.parse(configEl?.textContent || '');
  } catch {
    fail(root);
    return;
  }
  // Exactly as live passes them: parseInt() of the metafield, which is NaN when it is unset. The app
  // compares the party size with maxLimit, and a null there (what JSON can carry) refused every
  // booking with "exceeded the merchant's ticket availability"; NaN compares false, as on live.
  for (const key of ['minLimit', 'maxLimit']) config[key] = parseInt(config[key], 10);
  try {
    const app = await loadApp();
    // The app replaces the root's contents with the picker.
    root.querySelector('.experience-booking__loading')?.remove();
    app.enableBooking(config);
    root.removeAttribute('aria-busy');
  } catch (error) {
    console.warn('[experience-booking]', error);
    fail(root);
  }
}

for (const root of document.querySelectorAll('[data-experience-booking]')) start(/** @type {HTMLElement} */ (root));
